// Vite entry: mounts <App /> into #root.
//
// Phase 8 Task 8-6: Sentry SDK init (DSN 未設定なら no-op) + `Sentry.ErrorBoundary`
// で App tree を wrap. unhandled error は Sentry に流れ、ユーザには SentryFallback
// が出る (architecture.md §21 / consult 2026-05-06).

import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './presentation/App.js';
import { SentryFallback } from './presentation/components/SentryFallback.js';
import { initSentry, parseSampleRate } from './sentryInit.js';
import './presentation/styles.css';

// 2026-05-17 (wide-eraser-perf-investigation-2026-05-16.md §「第 2 ラウンド」):
// dev / e2e 限定で `@oekaki/drawing-core` の perf measure トグル + clearer を
// `window.__OEKAKI_PERF__` に bridge する. e2e harness
// (`packages/web/e2e/drawingPerfHarness.spec.ts`) がここから ON/OFF を切り替えて
// `eraseLine` / `drawLine` の `performance.measure` を収集する経路.
//
// `import.meta.env.DEV` は Vite の bundler-time const なので production bundle
// では if 全体ごと dead-code elimination される (= production に bridge は残らない).
// drawing-core 側の measure 関数自体は `let ENABLED = false` + boolean check で
// short-circuit するため、prod でこの bridge が呼ばれないかぎりオーバーヘッドは
// boolean read 1 個分.
//
// 2026-05-21 (PR 2-β follow-up,
// `docs/_wip/perf-pr2beta-worker-offload-2026-05-17.md` §「残課題」2):
// PR 2-β で描画コアが Worker thread に移行したので、main-thread の
// `enableDrawingPerfMeasure()` だけでは worker 内の hot path に effect が出ない.
// `__OEKAKI_PERF__` を engine 経由の worker round-trip に拡張し、harness の
// 既存 API 形 (`enableDrawingPerfMeasure` / `clearDrawingPerfMeasures` /
// `disableDrawingPerfMeasure`) は変えずに worker mode でも動くようにする.
// 加えて新 API `getCollectedDrawingMeasures()` を追加: 呼ぶと worker buffer を
// drain し、entries を main thread の `performance.measure` に再 emit する
// (= harness の既存 `getEntriesByType('measure')` 集計コードがそのまま動く).
if (import.meta.env.DEV) {
  void import('@oekaki/drawing-core').then((mod) => {
    type W = {
      __OEKAKI_PERF__?: {
        enableDrawingPerfMeasure: () => Promise<void>;
        disableDrawingPerfMeasure: () => Promise<void>;
        clearDrawingPerfMeasures: () => Promise<void>;
        isDrawingPerfMeasureEnabled: () => boolean;
        /** 描き味ベンチ専用: 入力→表示の遅れ記録の ON/OFF (main の入力バッファ + 各 worker)。 */
        setInputLatency: (enabled: boolean) => Promise<void>;
        /** 描き味ベンチ専用: 層サムネ機能の ON/OFF (全 engine)。 */
        setThumbnails: (enabled: boolean) => void;
        /** 描き味ベンチ専用: サムネの後回し段階を固定 (null = 自動)。 */
        setThumbnailTier: (tier: 'normal' | 'slow' | 'very-slow' | null) => void;
        /** 描き味ベンチ専用: サムネ 1 回分の処理時間の記録 ON/OFF。 */
        setThumbnailStats: (enabled: boolean) => Promise<void>;
        /** 描き味ベンチ専用: サムネ 1 回分の処理時間 (ms) を取り出す。 */
        collectThumbnailStats: () => Promise<readonly number[]>;
        /** 描き味ベンチ専用: 全 engine の worker から遅れ記録を取り出す。 */
        collectInputLatency: () => Promise<
          ReadonlyArray<{
            readonly first: boolean;
            readonly delayMs: number;
            readonly inputAt: number;
          }>
        >;
        /**
         * Collect measures from every live engine's worker (= drain its
         * internal buffer) AND re-emit each entry onto the main thread's
         * `performance.measure` timeline. After this resolves, the harness
         * can read aggregated drawing measures via
         * `performance.getEntriesByType('measure').filter(e =>
         *   e.name.startsWith('oekaki.drawing.'))` exactly as it did
         * pre-PR-2-β. Inline-mode engines are a no-op (their measures
         * already landed on the main timeline directly).
         */
        getCollectedDrawingMeasures: () => Promise<
          ReadonlyArray<{
            readonly name: string;
            readonly startTime: number;
            readonly duration: number;
          }>
        >;
      };
    };

    /**
     * `CanvasDrawingEngine` registers itself in `window.__OEKAKI_ENGINES__`
     * (a `{ engines: Set<...> }`) when `import.meta.env.DEV`. The bridge
     * uses that registry to route worker round-trips. We resolve the set
     * lazily on every call because engines come + go on mount/unmount
     * (= no need to subscribe to the registry, just walk it at call time).
     */
    type DevRegistry = {
      readonly engines: ReadonlySet<{
        readonly enablePerfMeasure: () => Promise<void>;
        readonly disablePerfMeasure: () => Promise<void>;
        readonly clearPerfMeasures: () => Promise<void>;
        readonly setInputLatency: (enabled: boolean) => Promise<void>;
        readonly setLayerThumbnailsEnabled: (enabled: boolean) => void;
        readonly setLayerThumbnailTier: (tier: 'normal' | 'slow' | 'very-slow' | null) => void;
        readonly setLayerThumbnailStats: (enabled: boolean) => Promise<void>;
        readonly collectLayerThumbnailStats: () => Promise<readonly number[]>;
        readonly collectInputLatency: () => Promise<
          ReadonlyArray<{
            readonly first: boolean;
            readonly delayMs: number;
            readonly inputAt: number;
          }>
        >;
        readonly collectPerfMeasures: () => Promise<
          ReadonlyArray<{
            readonly name: string;
            readonly startTime: number;
            readonly duration: number;
          }>
        >;
      }>;
    };
    const liveEngines = (): DevRegistry['engines'] => {
      const reg = (globalThis as unknown as { __OEKAKI_ENGINES__?: DevRegistry })
        .__OEKAKI_ENGINES__;
      return reg?.engines ?? new Set();
    };

    (window as unknown as W).__OEKAKI_PERF__ = {
      enableDrawingPerfMeasure: async (): Promise<void> => {
        // Flip the main-thread gate too: vitest unit tests + the inline
        // mode (= forceInline / no OffscreenCanvas) share the realm with
        // this module, so the measure path checks the main `ENABLED` flag.
        mod.enableDrawingPerfMeasure();
        // Flip every live engine's worker. Engines that boot inline
        // resolve immediately (= no-op), so the await is cheap.
        await Promise.all(Array.from(liveEngines(), (e) => e.enablePerfMeasure()));
      },
      disableDrawingPerfMeasure: async (): Promise<void> => {
        mod.disableDrawingPerfMeasure();
        await Promise.all(Array.from(liveEngines(), (e) => e.disablePerfMeasure()));
      },
      clearDrawingPerfMeasures: async (): Promise<void> => {
        // Reset main-thread state (= inline mode + DevTools-visible
        // entries) AND each worker. Both legs are idempotent.
        mod.clearDrawingPerfMeasures();
        await Promise.all(Array.from(liveEngines(), (e) => e.clearPerfMeasures()));
      },
      isDrawingPerfMeasureEnabled: mod.isDrawingPerfMeasureEnabled,
      setInputLatency: async (enabled: boolean): Promise<void> => {
        await Promise.all(Array.from(liveEngines(), (e) => e.setInputLatency(enabled)));
      },
      setThumbnails: (enabled: boolean): void => {
        for (const e of liveEngines()) e.setLayerThumbnailsEnabled(enabled);
      },
      setThumbnailTier: (tier): void => {
        for (const e of liveEngines()) e.setLayerThumbnailTier(tier);
      },
      setThumbnailStats: async (enabled: boolean): Promise<void> => {
        await Promise.all(Array.from(liveEngines(), (e) => e.setLayerThumbnailStats(enabled)));
      },
      collectThumbnailStats: async () => {
        const per = await Promise.all(
          Array.from(liveEngines(), (e) => e.collectLayerThumbnailStats()),
        );
        return per.flat();
      },
      collectInputLatency: async () => {
        const per = await Promise.all(Array.from(liveEngines(), (e) => e.collectInputLatency()));
        return per.flat();
      },
      getCollectedDrawingMeasures: async (): Promise<
        ReadonlyArray<{
          readonly name: string;
          readonly startTime: number;
          readonly duration: number;
        }>
      > => {
        // 1) Pull buffer from every live engine's worker (= 1 postMessage
        //    round-trip per engine, not per measure entry — bulk drain
        //    keeps IPC overhead bounded).
        const perEngine = await Promise.all(
          Array.from(liveEngines(), (e) => e.collectPerfMeasures()),
        );
        // 2) Also include any inline-mode entries from this realm's
        //    drawing-core buffer (= forceInline tests + browsers without
        //    OffscreenCanvas). These pile up the same `collected[]` array
        //    as the worker's, just on the main thread.
        const inline = mod.getCollectedDrawingMeasures();
        mod.clearCollectedDrawingMeasures();
        const all = [
          ...inline.map((e) => ({ name: e.name, startTime: e.startTime, duration: e.duration })),
          ...perEngine.flat(),
        ];
        // 3) Re-emit each entry as a main-thread `performance.measure` so
        //    the harness's existing `getEntriesByType('measure')` aggregator
        //    keeps working unchanged. Each entry needs a paired start mark
        //    with a unique name (= measure ranges can't overlap on the
        //    same mark name). We use `performance.measure(name, { start,
        //    duration })` form which is supported in Chrome 89+ and is the
        //    bundled DOM lib that vite/client types ship.
        //
        //    `startTime` is in the worker's `performance.now()` timebase
        //    (or in this main realm's timebase for inline entries). The
        //    harness only reads `duration` from each measure, so absolute
        //    times don't have to align — they just need to be non-negative
        //    and produce a measure entry the harness can filter on `name`.
        for (const e of all) {
          try {
            performance.measure(e.name, { start: e.startTime, duration: e.duration });
          } catch {
            // Defensive: some runtimes reject negative start. Fall back to
            // a 0-anchored measure that still carries the right duration.
            try {
              performance.measure(e.name, { start: 0, duration: e.duration });
            } catch {
              // last-resort no-op — we still return `all` so the harness
              // can read measures directly if it prefers.
            }
          }
        }
        return all;
      },
    };
  });
}

// T2 タイムラプス mp4 生成 (growth-timelapse-share-2026-07-02.md §5.2) —
// dev 限定の実機確認ハーネス. `window.__OEKAKI_TIMELAPSE__.generate({...})`
// で console から生成→自動 DL できる (本番 UI は T3). `import.meta.env.DEV`
// ガードで production bundle からは module ごと消える (__OEKAKI_PERF__ と
// 同パターン).
if (import.meta.env.DEV) {
  void import('./infrastructure/timelapse/devHarness.js').then((m) =>
    m.installTimelapseDevHarness(),
  );
}

// Sentry init は React mount **前**: mount 中の error も拾えるように.
// DSN 未設定 / test 環境では init skip され、ErrorBoundary は単に fallback UI
// を出すだけ (= Sentry には流れない, dev / CI の通常状態).
const sentryEnabled = initSentry({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  tracesSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
});

if (!sentryEnabled && import.meta.env.MODE !== 'test') {
  // dev / CI で「DSN 未設定なので Sentry に送らない」ことを 1 行で残す.
  // 本番に DSN を渡し忘れた事故を console で気付けるように.
  // eslint-disable-next-line no-console
  console.info('[sentry] init skipped (no DSN provided, mode=', import.meta.env.MODE, ')');
}

const root = document.getElementById('root');
if (root === null) {
  throw new Error('#root element not found in index.html');
}

createRoot(root).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<SentryFallback />}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
